import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { logActivity } from "@/lib/logActivity";
import Swal from "sweetalert2";
import type { Client, PortalToken } from "../types";

interface Props {
  client: Client;
  tenantId: string;
}

/** Generate a compact readable short code — e.g. "AB12-CD34" */
function generateShortCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const rand = (n: number) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  return `${rand(4)}-${rand(4)}`;
}

/** Generate a secure random URL-safe token */
function generateToken(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

export default function PortalAccessTab({ client, tenantId }: Props) {
  const [token, setToken]     = useState<PortalToken | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [copied, setCopied]   = useState(false);

  const fetchToken = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("client_access_tokens")
      .select("id, token, short_code, status, created_at, updated_at, expires_at")
      .eq("client_id", client.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setToken(data as PortalToken | null);
    setLoading(false);
  }, [client.id, tenantId]);

  useEffect(() => { fetchToken(); }, [fetchToken]);

  const portalUrl = token ? `${window.location.origin}/portal/${token.token}` : null;

  const handleCopy = async () => {
    if (!portalUrl) return;
    await navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  /* ── Deactivate / Reactivate ── */
  const handleToggleActive = async () => {
    if (!token) return;
    const isActive = token.status === "active";

    const res = await Swal.fire({
      title: isActive ? "Deactivate portal access?" : "Reactivate portal access?",
      text: isActive
        ? "The client won't be able to access the portal until you reactivate it."
        : `"${client.full_name}" will regain access to the portal.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: isActive ? "Deactivate" : "Reactivate",
      cancelButtonText: "Cancel",
      confirmButtonColor: isActive ? "#ef4444" : "#C8A96E",
      cancelButtonColor: "#64748b",
      background: "#ffffff",
      color: "#0f172a",
    });
    if (!res.isConfirmed) return;

    setWorking(true);
    const newStatus = isActive ? "inactive" : "active";
    const { error } = await supabase
      .from("client_access_tokens")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", token.id)
      .eq("tenant_id", tenantId);
    setWorking(false);

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#0f172a" });
      return;
    }
    setToken((prev) => prev ? { ...prev, status: newStatus } : null);
    logActivity({
      tenantId,
      action: isActive ? "deactivated" : "reactivated",
      entity: "portal_token",
      entityId: client.id,
      entityName: client.full_name,
    });
    Swal.fire({
      title: isActive ? "Deactivated" : "Reactivated!",
      icon: "success",
      timer: 1400,
      showConfirmButton: false,
      background: "#fff",
      color: "#0f172a",
    });
  };

  /* ── Regenerate (revokes current, creates new) ── */
  const handleRegenerate = async () => {
    const res = await Swal.fire({
      title: "Regenerate portal link?",
      text: "The old link will stop working immediately. You'll need to share the new link with the client.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Regenerate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      background: "#ffffff",
      color: "#0f172a",
    });
    if (!res.isConfirmed) return;

    // Revoke current token first
    if (token) {
      await supabase
        .from("client_access_tokens")
        .update({ status: "revoked", updated_at: new Date().toISOString() })
        .eq("id", token.id)
        .eq("tenant_id", tenantId);
    }

    await createNewToken();
  };

  /* ── Create brand new token ── */
  const createNewToken = async () => {
    setWorking(true);
    const { data, error } = await supabase
      .from("client_access_tokens")
      .insert({
        tenant_id:  tenantId,
        client_id:  client.id,
        token:      generateToken(),
        short_code: generateShortCode(),
        status:     "active",
      })
      .select("id, token, short_code, status, created_at, updated_at, expires_at")
      .maybeSingle();

    setWorking(false);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#0f172a" });
      return;
    }
    setToken(data as PortalToken);
    logActivity({
      tenantId,
      action: "created",
      entity: "portal_token",
      entityId: client.id,
      entityName: client.full_name,
    });
    Swal.fire({
      title: "Portal link created!",
      text: "Copy and share it with the client.",
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
      background: "#fff",
      color: "#0f172a",
    });
  };

  /* ── Revoke ── */
  const handleRevoke = async () => {
    if (!token) return;
    const res = await Swal.fire({
      title: "Revoke portal access?",
      text: "Client will lose access permanently with this link. You can always generate a new one.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, revoke",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      background: "#ffffff",
      color: "#0f172a",
    });
    if (!res.isConfirmed) return;
    setWorking(true);
    await supabase
      .from("client_access_tokens")
      .update({ status: "revoked", updated_at: new Date().toISOString() })
      .eq("id", token.id)
      .eq("tenant_id", tenantId);
    setWorking(false);
    setToken((prev) => prev ? { ...prev, status: "revoked" } : null);
    logActivity({ tenantId, action: "revoked", entity: "portal_token", entityId: client.id, entityName: client.full_name });
    Swal.fire({ title: "Revoked", icon: "success", timer: 1400, showConfirmButton: false, background: "#fff", color: "#0f172a" });
  };

  const isActive   = token?.status === "active";
  const isInactive = token?.status === "inactive";
  const isRevoked  = token?.status === "revoked";

  const statusBadge = () => {
    if (loading) return { cls: "bg-slate-100 text-slate-400 border-slate-200", dot: "bg-slate-300", label: "Loading…" };
    if (!token)  return { cls: "bg-slate-100 text-slate-400 border-slate-200", dot: "bg-slate-300", label: "No access" };
    if (isActive)   return { cls: "bg-emerald-400/10 text-emerald-600 border-emerald-400/20", dot: "bg-emerald-400 animate-pulse", label: "Active" };
    if (isInactive) return { cls: "bg-amber-400/10 text-amber-600 border-amber-400/20", dot: "bg-amber-400", label: "Inactive" };
    return { cls: "bg-red-400/10 text-red-500 border-red-400/20", dot: "bg-red-400", label: "Revoked" };
  };

  const badge = statusBadge();

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Main card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8A96E]/10">
              <i className="ri-shield-keyhole-line text-[#9a7840] text-lg"></i>
            </div>
            <div>
              <h3 className="text-slate-800 font-semibold text-sm">Client Portal Access</h3>
              <p className="text-slate-400 text-xs mt-0.5">Secure link for client to access contracts, jobs &amp; files</p>
            </div>
          </div>
          <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${badge.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`}></span>
            {badge.label}
          </span>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-400 rounded-full animate-spin"></div>
            Loading portal status…
          </div>
        ) : token ? (
          <div className="space-y-4">
            {/* URL box */}
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-slate-400 text-[10px] uppercase tracking-wider">Portal URL</p>
                {token.short_code && (
                  <span className="text-slate-400 text-[10px] font-mono bg-white border border-slate-200 px-2 py-0.5 rounded">
                    {token.short_code}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <p className={`flex-1 text-sm font-mono truncate ${isActive ? "text-slate-700" : "text-slate-400 line-through"}`}>
                  {portalUrl}
                </p>
                {isActive && (
                  <button
                    onClick={handleCopy}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                      copied
                        ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                        : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
                    {copied ? "Copied!" : "Copy"}
                  </button>
                )}
              </div>
            </div>

            {/* Meta row */}
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
                <p className="text-slate-400 mb-0.5">Status</p>
                <p className={`font-semibold capitalize ${isActive ? "text-emerald-600" : isInactive ? "text-amber-600" : "text-red-500"}`}>
                  {token.status}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
                <p className="text-slate-400 mb-0.5">Created</p>
                <p className="text-slate-700 font-medium">
                  {new Date(token.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
                <p className="text-slate-400 mb-0.5">Expires</p>
                <p className="text-slate-700 font-medium">
                  {token.expires_at
                    ? new Date(token.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : "Never"}
                </p>
              </div>
            </div>

            {/* Revoked / Inactive banner */}
            {(isRevoked || isInactive) && (
              <div className={`flex items-center gap-2.5 rounded-lg px-4 py-3 text-xs ${
                isRevoked ? "bg-red-50 border border-red-200 text-red-600" : "bg-amber-50 border border-amber-200 text-amber-700"
              }`}>
                <i className={isRevoked ? "ri-close-circle-line" : "ri-pause-circle-line"}></i>
                <span>
                  {isRevoked
                    ? "This link is permanently revoked. Generate a new one to restore access."
                    : "Access is temporarily deactivated. Reactivate to restore client access."}
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap pt-1">
              {isActive && (
                <a
                  href={portalUrl ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  Open Portal
                </a>
              )}

              {/* Toggle active/inactive — only when not revoked */}
              {!isRevoked && (
                <button
                  onClick={handleToggleActive}
                  disabled={working}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                    isActive
                      ? "bg-white hover:bg-amber-50 text-amber-600 border-amber-200"
                      : "bg-white hover:bg-emerald-50 text-emerald-600 border-emerald-200"
                  }`}
                >
                  {working
                    ? <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    : <i className={isActive ? "ri-pause-circle-line" : "ri-play-circle-line"}></i>
                  }
                  {isActive ? "Deactivate" : "Reactivate"}
                </button>
              )}

              {/* Regenerate — always available */}
              <button
                onClick={handleRegenerate}
                disabled={working}
                className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-slate-50 text-slate-600 text-sm font-medium border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {working
                  ? <div className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  : <i className="ri-refresh-line"></i>
                }
                Regenerate
              </button>

              {/* Revoke — only active/inactive */}
              {!isRevoked && (
                <button
                  onClick={handleRevoke}
                  disabled={working}
                  className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-red-50 text-red-500 text-sm font-medium border border-red-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-shield-cross-line"></i>
                  Revoke
                </button>
              )}

              {/* If revoked — offer to create a new one */}
              {isRevoked && (
                <button
                  onClick={createNewToken}
                  disabled={working}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {working
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <i className="ri-shield-keyhole-line"></i>
                  }
                  Generate New Link
                </button>
              )}
            </div>
          </div>
        ) : (
          /* ── No token yet ── */
          <div className="text-center py-8">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100 mx-auto mb-3">
              <i className="ri-lock-line text-slate-400 text-xl"></i>
            </div>
            <p className="text-slate-700 font-medium text-sm mb-1">No portal access yet</p>
            <p className="text-slate-400 text-xs mb-5 max-w-xs mx-auto">
              Generate a secure link so <strong>{client.full_name}</strong> can access their contracts, jobs, and media files.
            </p>
            <button
              onClick={createNewToken}
              disabled={working}
              className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {working
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <i className="ri-shield-keyhole-line"></i>
              }
              Generate Portal Link
            </button>
          </div>
        )}
      </div>

      {/* Token history hint */}
      {token && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">How validation works</p>
          </div>
          <div className="px-5 py-4 space-y-2.5">
            {[
              { icon: "ri-checkbox-circle-line", color: "text-emerald-500", label: "Token exists in database", ok: !!token },
              { icon: "ri-checkbox-circle-line", color: "text-emerald-500", label: `Status is "active"`, ok: isActive },
              { icon: "ri-checkbox-circle-line", color: "text-emerald-500", label: "No expiry date (or expiry is in the future)", ok: !token.expires_at || new Date(token.expires_at) > new Date() },
              { icon: "ri-checkbox-circle-line", color: "text-emerald-500", label: "Links to correct client_id", ok: true },
            ].map((row, i) => (
              <div key={i} className="flex items-center gap-2.5 text-xs">
                <span className={`w-4 h-4 flex items-center justify-center shrink-0 ${row.ok ? "text-emerald-500" : "text-red-400"}`}>
                  <i className={row.ok ? "ri-checkbox-circle-line" : "ri-close-circle-line"}></i>
                </span>
                <span className={row.ok ? "text-slate-600" : "text-red-500"}>{row.label}</span>
                <span className={`ml-auto font-semibold ${row.ok ? "text-emerald-500" : "text-red-400"}`}>
                  {row.ok ? "Pass" : "Fail"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-[#C8A96E]/5 rounded-xl border border-[#C8A96E]/15 p-4 flex items-start gap-3">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/15 shrink-0 mt-0.5">
          <i className="ri-information-line text-[#9a7840] text-sm"></i>
        </div>
        <div>
          <p className="text-slate-700 text-sm font-medium mb-0.5">How the client portal works</p>
          <p className="text-slate-500 text-xs leading-relaxed">
            Share the portal URL with your client via email or message. They can view contracts, sign digitally, track job progress, review photo selections, download deliveries, and access their digital invitation — no account needed.
          </p>
        </div>
      </div>
    </div>
  );
}